import React, { useContext } from 'react';
import { observer } from 'mobx-react';
import _ from 'lodash';
import { styled } from '@this/constants/themes';
import {
  Button,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Typography,
  CircularProgress as OriginalCircularProgress,
  Box
} from '@material-ui/core';
import { CheckCircle as CheckCircleIcon, Block as BlockIcon } from '@material-ui/icons';
import AppointContext from '@this/components/appoint/appoint_context';
import type { AdjustmentStatus } from '@this/domain/appoint/adjustment';
import type Adjustment from '@this/domain/appoint/adjustment';
import type { AdjustmentFinishedType, AdjustmentsByStatusType } from '@this/components/appoint/common';
import {
  DrawerIn,
  DrawerTitle,
  DrawerBody,
  CloseButton,
  drawerSidePadding
} from '@this/components/appoint/common';
import { SwapHorizontalIcon } from '@this/components/appoint/icons';
import muiTheme from '@this/components/appoint/mui_theme';

interface Props {
  open: boolean;
  onClose: () => void;
  adjustments: AdjustmentsByStatusType;
  selectedAdjustment: Adjustment | null;
  currentStatusTab: AdjustmentStatus;
  onCurrentStatusTabChange: (status: AdjustmentStatus) => void;
  adjustmentsFinished: AdjustmentFinishedType;
  isAdjustmentLoading: boolean;
  containerRef: React.RefObject<HTMLDivElement>;
  adjustmentsLoader: React.MutableRefObject<HTMLLIElement | null>;
  onDetailOpen: (key?: string) => void;
}

const AdjustmentDrawer = observer(
  ({
    open,
    onClose,
    adjustments,
    selectedAdjustment,
    currentStatusTab,
    onCurrentStatusTabChange,
    adjustmentsFinished,
    isAdjustmentLoading,
    containerRef,
    adjustmentsLoader,
    onDetailOpen
  }: Props) => {
    const contextValue = useContext(AppointContext);

    return (
      <Drawer
        open={open}
        anchor="left"
        variant="persistent"
        onClick={e => e.stopPropagation()}
        onClose={() => onClose()}
        PaperProps={{
          style: {
            position: contextValue.isMobile ? 'fixed' : 'absolute',
            zIndex: 100,
            overflow: 'hidden',
            boxSizing: 'border-box',
            minWidth: contextValue.isMobile ? '100vw' : '100%',
            borderTop: 'solid 10px #243565',
            borderRight: '0'
          }
        }}
        BackdropProps={{ style: { position: 'absolute' } }}
        ModalProps={{
          container: containerRef.current,
          style: { position: 'absolute' }
        }}
      >
        <DrawerIn>
          <DrawerTitle>
            <Box>調整一覧</Box>
            {!selectedAdjustment && <CloseButton onClick={() => onClose()} />}
          </DrawerTitle>
          <DrawerBody>
            <TheStatusButtonWrap>
              <TheStatusButton
                onClick={() => onCurrentStatusTabChange('adjusting')}
                current={`${currentStatusTab === 'adjusting'}`}
              >
                <SwapHorizontalIcon
                  color={currentStatusTab === 'adjusting' ? muiTheme.palette.primary.main : '#909090'}
                  width="16px"
                  height="16px"
                />
                <TheStatusButtonLabel>調整中</TheStatusButtonLabel>
              </TheStatusButton>
              <TheStatusButton
                onClick={() => onCurrentStatusTabChange('fixed')}
                current={`${currentStatusTab === 'fixed'}`}
              >
                <CheckCircleIcon />
                <TheStatusButtonLabel>確定</TheStatusButtonLabel>
              </TheStatusButton>
              <TheStatusButton
                onClick={() => onCurrentStatusTabChange('cancelled')}
                current={`${currentStatusTab === 'cancelled'}`}
              >
                <BlockIcon />
                <TheStatusButtonLabel>キャンセル</TheStatusButtonLabel>
              </TheStatusButton>
            </TheStatusButtonWrap>
            {adjustmentsFinished[currentStatusTab] && adjustments[currentStatusTab].length === 0 ? (
              <NoAdjustments>{`${
                currentStatusTab === 'adjusting'
                  ? '調整中の'
                  : currentStatusTab === 'fixed'
                  ? '確定された'
                  : '削除された'
              }予定はありません。`}</NoAdjustments>
            ) : (
              <TheList>
                {_.sortBy(adjustments[currentStatusTab], a => a.startDate())
                  .reverse()
                  .map((a, i) => (
                    <TheListItemWrap key={`adjustment_${i}`} selected={`${a.key === selectedAdjustment?.key}`}>
                      <ListItem
                        button
                        onClick={() => onDetailOpen(a.key)}
                        key={a.id}
                        style={{ display: 'block', padding: '6px' }}
                      >
                        <TheListItemText
                          disableTypography
                          primary={
                            <>
                              <TheAdjustmentTitle>{a.title}</TheAdjustmentTitle>
                              <TheAdjustmentDate>{a.dateText()}</TheAdjustmentDate>
                              {a.adjusting && a.isPast && (
                                <TheAdjustmentPast>期間が過ぎています</TheAdjustmentPast>
                              )}
                            </>
                          }
                        />
                        {/* TODO: 候補日/確定日と参加者 */}
                        {/* <Box>
                            <Box display="flex" style={{ alignItems: 'center' }}>
                              <ScheduleIcon style={{ color: muiTheme.palette.grey[600], fontSize: '14px' }} />
                              <Typography color="textSecondary" style={{ fontSize: '14px' }}>
                                候補日：{a.candidates[0] && a.candidates[0].start.format('YYYY/MM/DD hh:mm〜')}
                              </Typography>
                            </Box>
                          </Box> */}
                      </ListItem>
                    </TheListItemWrap>
                  ))}
                <LoadingLi
                  className="loading"
                  ref={adjustmentsLoader}
                  style={{ display: adjustmentsFinished[currentStatusTab] ? 'none' : 'block' }}
                >
                  <span>Loading...</span>
                  {isAdjustmentLoading && <AdjustmentProgress />}
                </LoadingLi>
              </TheList>
            )}
          </DrawerBody>
        </DrawerIn>
      </Drawer>
    );
  }
);

const TheStatusButtonWrap = styled.div`
  display: flex;
  position: relative;
  padding: 0 ${drawerSidePadding};
`;

const TheStatusButton = styled(Button)<{ current: string }>`
  display: flex;
  flex-grow: 1;
  padding: 2px 6px;
  color: #909090;
  opacity: 0.3;
  &:hover {
    opacity: 1;
  }
  ${props =>
    props.current === 'true' &&
    `
    color: ${muiTheme.palette.primary.main};
    background: ${muiTheme.palette.primary.light};
    opacity: 1;
  `}

  .MuiSvgIcon-root {
    width: 16px;
    height: 16px;
  }
`;

const TheStatusButtonLabel = styled(Typography)`
  margin-left: 5px;
  font-size: 10px;
  font-weight: 500;
  line-height: 32px;
`;

const NoAdjustments = styled(Typography)`
  padding: 14px ${drawerSidePadding};
  font-size: 12px;
  color: ${props => props.theme.grayTextColor};
`;

const TheList = styled(List)`
  overflow-y: auto;
  max-height: calc(100vh - 54px - 10px - 20px - 24px - 36px - 24px);
  padding: 0 ${drawerSidePadding};
`;

const TheListItemWrap = styled.li<{ selected: string }>`
  margin-top: 10px;
  border: solid 1px #ededed;
  border-radius: 3px;

  ${props =>
    props.selected === 'true' &&
    `
      color: #fff;
      background-color: ${muiTheme.palette.primary.main};
  `}
`;

const TheListItemText = styled(ListItemText)`
  overflow: hidden;
  margin: 0;
`;

const TheAdjustmentTitle = styled.div`
  margin-bottom: 4px;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.8;
`;

const TheAdjustmentDate = styled.div`
  font-size: 12px;
  line-height: 1.8;
`;

const TheAdjustmentPast = styled.div`
  color: #bf0000;
  font-size: 12px;
  line-height: 1.4;
`;

const LoadingLi = styled.li`
  position: relative;
  padding: 12px 0;
`;

const AdjustmentProgress = styled(OriginalCircularProgress).attrs({ color: 'primary', size: 24 })`
  position: absolute;
  top: 50%;
  right: 12px;
  margin-top: -12px;
`;

export default AdjustmentDrawer;
